import React, { useState, useEffect, useRef } from 'react';
import { storage } from '../firebase';
import { ref, getDownloadURL, uploadBytes, deleteObject } from 'firebase/storage';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaSpinner, FaUpload, FaTrash, FaExpand, FaTimes, FaEye } from 'react-icons/fa';
import ArtmosphereFeedbackModal from './ArtmosphereFeedbackModal';
import LearningSpaceShareModal from './LearningSpaceShareModal';
import { saveDailyActivity, checkDailyActivityExists, updateDailyActivityFeedback } from '../utils/activityService';
import dailyActivityService, { findDailyActivityFromList } from '../services/dailyActivityService';
import { backendConfig } from '../config/backendConfig';
import { useFeatureQuota } from '../hooks/useFeatureQuota';
import UpgradeQuotaModal from './UpgradeQuotaModal';

const PDFPageDisplayForDailyActs = ({
  currentActivityId,
  onPagesLoaded,
  isExpanded = false,
  onClose,
  onShareFlowComplete,
  onNextActivity,
  onReady,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { checkQuotaBeforeUse, recordUsage, showUpgradeModal, dismissUpgradeModal, upgradeModalFeature } = useFeatureQuota();
  const [pageImages, setPageImages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [overlayUrl, setOverlayUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [existingActivity, setExistingActivity] = useState(null);
  const [activityCompleted, setActivityCompleted] = useState(false);
  const [showFeedbackPreview, setShowFeedbackPreview] = useState(false);
  const [currentActivityData, setCurrentActivityData] = useState(null);
  const [showLearningSpaceModal, setShowLearningSpaceModal] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [shareModalCompleted, setShareModalCompleted] = useState(false); // Track if share modal has been completed

  // Quota required only for Artmosphere feedback (not for attempting activities)
  const requestArtmosphereFeedback = async () => {
    const quotaCheck = await checkQuotaBeforeUse('dailyActivities');
    if (!quotaCheck.allowed) return;
    await recordUsage('dailyActivities');
    setShowFeedbackModal(true);
  };

  // Use the prop if provided, otherwise use internal state
  const expanded = isExpanded !== undefined ? isExpanded : internalExpanded;
  const hasSignaledReadyRef = useRef(false);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setInternalExpanded(false);
    }
  };

  // Notify parent once the first page is ready in expanded mode,
  // with a small delay so the loader veil is actually visible.
  useEffect(() => {
    if (
      typeof onReady === 'function' &&
      expanded &&
      !loading &&
      pageImages.length > 0 &&
      !hasSignaledReadyRef.current
    ) {
      hasSignaledReadyRef.current = true;
      const timer = setTimeout(() => {
        onReady();
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [onReady, expanded, loading, pageImages.length]);

  useEffect(() => {
    if (!expanded || !onClose) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [expanded, onClose]);

  useEffect(() => {
    if (user) {
      // Load PDF pages immediately - this is the main content
      loadPDFPages();
      // Make checkExistingActivity non-blocking - run in background, don't wait
      // Use setTimeout to ensure it doesn't block the initial render
      setTimeout(() => {
        checkExistingActivity().catch(err => {
          // Silently handle errors - this is background data
          if (err.name !== 'AbortError') {
            console.warn('⚠️ Background check for existing activity failed (non-blocking):', err.message);
          }
        });
      }, 0);
    }
  }, [user, currentActivityId]);

  useEffect(() => {
    if (pageImages.length > 0) {
      loadOverlayForPage();
    }
  }, [currentPage, pageImages]);

  // Load current activity data for feedback
  useEffect(() => {
    const loadCurrentActivityData = async () => {
      try {
        const allActivities = await dailyActivityService.getAllActivities();
        const currentActivity = findDailyActivityFromList(allActivities, currentActivityId);
        
        if (currentActivity) {
          setCurrentActivityData({
            title: currentActivity.title,
            description: currentActivity.description,
            prerequisites: currentActivity.materials || currentActivity.prerequisites || "Art supplies and imagination",
            steps: currentActivity.instructions || currentActivity.steps || "Follow the activity instructions and create your artwork"
          });
        } else {
          // Fallback to generic data if activity not found
          setCurrentActivityData({
            title: `Activity ${currentActivityId}`,
            description: "Daily creative activity for developing artistic thinking",
            prerequisites: "Art supplies and imagination",
            steps: "Follow the activity instructions and create your artwork"
          });
        }
      } catch (error) {
        console.error('Error loading current activity data:', error);
        // Fallback to generic data
        setCurrentActivityData({
          title: `Activity ${currentActivityId}`,
          description: "Daily creative activity for developing artistic thinking",
          prerequisites: "Art supplies and imagination",
          steps: "Follow the activity instructions and create your artwork"
        });
      }
    };

    if (currentActivityId) {
      loadCurrentActivityData();
    }
  }, [currentActivityId]);

  const checkExistingActivity = async () => {
    if (!user || !currentActivityId) return;
    
    try {
      // Check both Firestore and user progress for completion status
      const existing = await checkDailyActivityExists(currentActivityId);
      setExistingActivity(existing);
      
      // Only set activityCompleted to true if there's an uploaded artwork AND it's marked as completed
      // If user just uploaded artwork, don't mark as completed yet
      if (existing && existing.isCompleted && existing.userArtworkUrl) {
        // Activity is completed and has artwork - check if overlayUrl matches
        // If overlayUrl exists and matches, then it's truly completed
        // Otherwise, user might have uploaded new artwork
        if (overlayUrl && overlayUrl === existing.userArtworkUrl) {
          setActivityCompleted(true);
          console.log(`✅ Activity ${currentActivityId} is marked as completed with matching artwork`);
        } else {
          // Artwork URL doesn't match or no overlayUrl - might be new upload
          setActivityCompleted(false);
          console.log(`🔄 Activity ${currentActivityId} has existing completion but artwork may be new`);
        }
      } else {
        // No existing completion or no artwork - definitely not completed
        setActivityCompleted(false);
        console.log(`❌ Activity ${currentActivityId} is not completed`);
      }
      
      // Check if activity is completed in user progress - use backend config for correct URL
      try {
        const backendUrl = backendConfig.baseUrl;
        const userProgressUrl = `${backendUrl}/api/user-progress/${user.uid}`;
        
        // Use a timeout to prevent blocking - max 2 seconds
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2000);
        
        const response = await fetch(userProgressUrl, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const result = await response.json();
          const userProgress = result.progress;
          
          const isCompletedInProgress = userProgress?.completedActivities?.some(
            completed => completed.activityId === currentActivityId
          );
          
          // Only set to completed if progress says so AND we have matching artwork
          if (isCompletedInProgress && overlayUrl) {
            // Double-check that overlayUrl matches what's in progress
            // If it does, then it's truly completed
            setActivityCompleted(true);
            console.log(`✅ Activity ${currentActivityId} is marked as completed in progress`);
          } else if (!overlayUrl) {
            // No artwork uploaded yet - definitely not completed
            setActivityCompleted(false);
          }
        }
      } catch (progressError) {
        // Silently fail - this is a background check, don't block the UI
        if (progressError.name !== 'AbortError') {
          console.warn('⚠️ User progress check failed (non-blocking):', progressError.message);
        }
        // Don't set activityCompleted based on progress check if it failed
      }
    } catch (error) {
      // Silently fail - this is a background check
      console.warn('⚠️ Error checking existing activity (non-blocking):', error.message);
      // Don't set activityCompleted on error - let it default to false
    }
  };

  const loadPDFPages = async () => {
    try {
      setLoading(true);
      
      // OPTIMIZATION: Use cached activities if available to avoid slow Firestore query
      // This significantly reduces PDF loading time
      let allActivities;
      try {
        // Try to get from cache first (non-blocking)
        allActivities = await dailyActivityService.getAllActivities(false); // false = use cache if available
      } catch (cacheError) {
        console.warn('⚠️ Cache fetch failed, loading fresh:', cacheError);
        // Fallback to fresh load if cache fails
        allActivities = await dailyActivityService.getAllActivities(true);
      }
      
      const currentActivity = findDailyActivityFromList(allActivities, currentActivityId);
      
      if (!currentActivity || !currentActivity.imageUrl) {
        console.log(`❌ No activity found or no imageUrl for activity ${currentActivityId}`);
        setPageImages([]);
        setLoading(false);
        return;
      }
      
      // Use the imageUrl from Firestore directly so browser cache can make modal opening instant.
      const url = currentActivity.imageUrl;
      
      // Extract filename from URL for logging
      const urlParts = currentActivity.imageUrl.split('/');
      const filename = urlParts[urlParts.length - 1].split('?')[0]; // Remove query params
      
      const pageUrls = [{ 
        pageNumber: currentActivityId, 
        url, 
        filename: filename 
      }];
      
      setPageImages(pageUrls);
      console.log(`✅ Loaded activity ${currentActivityId}: ${filename}`);
      
      if (onPagesLoaded) {
        onPagesLoaded(1); // Only 1 page for this activity
      }
      
    } catch (error) {
      console.error(`❌ Error loading activity ${currentActivityId}:`, error);
      setPageImages([]);
    } finally {
      setLoading(false);
    }
  };

  const loadOverlayForPage = async () => {
    if (!user || !currentActivityId) return;
    
    try {
      // Try to load overlay for current page - use user-submissions for user artwork
      const overlayRef = ref(storage, `user-submissions/${user.uid}/activity_${currentActivityId}/overlay.jpg`);
      const url = await getDownloadURL(overlayRef);
      setOverlayUrl(url);
      console.log(`✅ Loaded overlay for activity ${currentActivityId}`);
    } catch (error) {
      console.log(`❌ No overlay found for activity ${currentActivityId}`);
      setOverlayUrl(null);
    }
  };

  // Compress image before upload to reduce upload time and storage
  const compressImage = (file, maxWidth = 1920, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        // Add timeout to prevent hanging on very large images (15 seconds)
        const timeoutId = setTimeout(() => {
          reject(new Error('Image processing timeout - image too large'));
        }, 15000);
        
        img.onload = () => {
          clearTimeout(timeoutId);
          
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const originalSize = file.size;
                const compressedSize = blob.size;
                const compressionRatio = ((1 - compressedSize / originalSize) * 100).toFixed(1);
                console.log(`📸 Image compressed: ${(originalSize / 1024 / 1024).toFixed(2)}MB → ${(compressedSize / 1024 / 1024).toFixed(2)}MB (${compressionRatio}% reduction)`);
                resolve(blob);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        
        img.onerror = () => {
          clearTimeout(timeoutId);
          reject(new Error('Failed to load image'));
        };
        
        img.src = e.target.result;
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      
      reader.readAsDataURL(file);
    });
  };

  const handleOverlayUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      
      // Reset completion states when uploading new artwork
      // This ensures buttons show correctly after upload
      setActivityCompleted(false);
      setShareModalCompleted(false);
      
      console.log('📤 Starting image upload...');
      console.log(`📊 Original file size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      
      // Compress image before upload to reduce upload time
      let fileToUpload = file;
      try {
        console.log('🔄 Compressing image...');
        const compressedBlob = await compressImage(file, 1920, 0.85);
        fileToUpload = compressedBlob;
        console.log('✅ Image compressed successfully');
      } catch (compressError) {
        console.warn('⚠️ Image compression failed, using original file:', compressError);
        // Continue with original file if compression fails
      }
      
      // Upload to Firebase Storage - use user-submissions for user artwork
      console.log('☁️ Uploading to Firebase Storage...');
      const overlayRef = ref(storage, `user-submissions/${user.uid}/activity_${currentActivityId}/overlay.jpg`);
      
      // Upload with timeout protection
      const uploadPromise = uploadBytes(overlayRef, fileToUpload);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Upload timeout - please check your connection')), 60000)
      );
      
      await Promise.race([uploadPromise, timeoutPromise]);
      console.log('✅ File uploaded to Storage');
      
      // Get download URL
      console.log('🔗 Getting download URL...');
      const url = await getDownloadURL(overlayRef);
      setOverlayUrl(url);
      
      // Don't save to Firestore here - only save when marking complete
      // This prevents duplicate saves
      console.log('✅ Artwork uploaded successfully — tap Mark Complete to finish');
      
    } catch (error) {
      console.error('❌ Error uploading artwork:', error);
      console.error('❌ Error details:', error.message, error.stack);
      
      // Provide user-friendly error message
      if (error.message.includes('timeout')) {
        alert('Upload is taking too long. Please check your internet connection and try again with a smaller image.');
      } else {
        alert('Failed to upload artwork. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveArtwork = async () => {
    if (!overlayUrl) return;
    
    try {
      // Delete from Firebase Storage - use user-submissions for user artwork
      const overlayRef = ref(storage, `user-submissions/${user.uid}/activity_${currentActivityId}/overlay.jpg`);
      await deleteObject(overlayRef);
      
      setOverlayUrl(null);
      console.log('✅ Artwork removed successfully');
      
    } catch (error) {
      console.error('Error removing artwork:', error);
    }
  };

  const handleMarkComplete = async () => {
    try {
      // DO NOT save to Firestore or mark as complete here
      // Only show the share modal - submission will be created when user chooses share/private
      console.log('✅ Showing share modal - submission will be created when user chooses share option');
      
      // Show Learning Space share modal (only if artwork was uploaded)
      if (overlayUrl) {
        setUploadedImageUrl(overlayUrl);
        setShowLearningSpaceModal(true);
        // Don't set activityCompleted yet - wait for share modal completion
      } else {
        // If no artwork uploaded, there's nothing to share, so just mark as completed
        // But still don't create submission - just mark in user progress
        try {
          await dailyActivityService.completeActivity(user.uid, currentActivityId);
          console.log('✅ Activity marked as complete in user progress (no artwork to share)');
        } catch (progressError) {
          console.warn('⚠️ Could not update user progress via backend:', progressError);
        }
        
        setActivityCompleted(true);
        setShareModalCompleted(true);
        if (onNextActivity) {
          console.log('🔄 Calling onNextActivity callback...');
          try {
            const result = await onNextActivity();
            console.log('✅ onNextActivity callback completed, result:', result);
          } catch (callbackError) {
            console.error('❌ Error in onNextActivity callback:', callbackError);
            // Fallback: reload page if callback fails
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        } else {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      }
      
    } catch (error) {
      console.error('❌ Error in handleMarkComplete:', error);
      alert('Failed to proceed. Please try again.');
    }
  };

  const handleFeedbackComplete = async (feedback) => {
    try {
      // Get activity details
      const activities = await dailyActivityService.getAllActivities();
      const currentActivity = findDailyActivityFromList(activities, currentActivityId);
      
      // Update activity with feedback (but DON'T mark as completed yet - wait for user to click "Mark Complete")
      await saveDailyActivity({
        activityId: currentActivityId,
        activityTitle: currentActivity?.title || `Daily Art Activity ${currentActivityId}`,
        activityDescription: currentActivity?.description || "Daily creative activity for developing artistic thinking",
        userArtworkUrl: overlayUrl,
        activityPageUrl: pageImages[currentPage - 1]?.url || '',
        feedback: feedback,
        isCompleted: false, // Don't mark as completed yet - user needs to click "Mark Complete"
        completedAt: null,
        metadata: {
          prerequisites: currentActivity?.materials || "Art supplies and imagination",
          steps: currentActivity?.instructions || "Follow the activity instructions and create your artwork",
          category: currentActivity?.category || "Creative Expression",
          difficulty: currentActivity?.difficulty || "Beginner"
        }
      });
      
      console.log('✅ Feedback saved successfully');
      
      // Close feedback modal - user will see "Mark Complete" and "Re-upload" buttons
      setShowFeedbackModal(false);
      
      // Refresh existing activity to show updated feedback
      const existing = await checkDailyActivityExists(currentActivityId);
      setExistingActivity(existing);
      
    } catch (error) {
      console.error('Error saving feedback:', error);
    }
  };

  const handleReuploadBasedOnFeedback = async () => {
    try {
      // Remove existing artwork
      if (overlayUrl) {
        await handleRemoveArtwork();
      }
      
      // Reset activity completed state so user can upload new artwork
      setActivityCompleted(false);
      setShareModalCompleted(false);
      
      // Close any modals
      setShowFeedbackModal(false);
      
      console.log('✅ Ready for re-upload based on feedback');
    } catch (error) {
      console.error('Error preparing for re-upload:', error);
    }
  };

  const nextPage = () => {
    if (currentPage < pageImages.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Don't show "No PDF pages found" when expanded - show the activity anyway
  if (pageImages.length === 0 && !expanded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🎨</div>
          <h3 className="text-xl font-semibold text-amber-700 mb-2">No PDF pages found</h3>
          <p className="text-amber-600 mb-6">The activity preview is available, but the full PDF page hasn't been uploaded yet.</p>
          
          {/* Back button to return to activity list */}
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg shadow-lg font-medium"
          >
            ← Back to Activities
          </button>
          
          <div className="mt-6 text-sm text-amber-500">
            <p>To see the full activity:</p>
            <p>1. Go to the PDF Uploader page</p>
            <p>2. Upload your PDF pages as images</p>
            <p>3. Return here to view the complete activity</p>
          </div>
        </div>
      </div>
    );
  }

  // Debug logging
  console.log('PDFPageDisplayForDailyActs render:', {
    expanded,
    pageImagesLength: pageImages.length,
    currentActivityId,
    loading
  });

  return (
    <div className={expanded ? 'fixed inset-0 z-[60] flex flex-col bg-[#FFF3D1]/70 backdrop-blur-md' : 'min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100'}>
      {expanded ? (
        <>
          <button
            type="button"
            onClick={handleClose}
            className="fixed right-3 top-3 z-[80] flex h-11 w-11 items-center justify-center rounded-full border-2 border-white bg-amber-500 text-white shadow-lg transition hover:bg-amber-600 sm:right-4 sm:top-4"
            title="Close activity"
            aria-label="Close activity"
          >
            <FaTimes size={18} />
          </button>
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain p-3 pt-14 sm:p-6 sm:pt-16">
          <div className="relative mx-auto my-auto w-full max-w-6xl rounded-[32px] border-2 border-amber-300 bg-white/95 px-4 py-3 shadow-2xl sm:px-6 sm:py-5 md:px-8 md:py-6">
            {/* Main Content Area */}
            <div className="w-full">
              {/* PDF Image Container with Overlay */}
              {pageImages.length > 0 && pageImages[currentPage - 1]?.url ? (
                <div className="relative group max-w-5xl mx-auto">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-400/30 to-orange-400/30 rounded-3xl blur-2xl"></div>
                  
                  {/* Main container */}
                  <div className="relative bg-gradient-to-br from-amber-100 to-orange-100 rounded-3xl p-4 sm:p-6 shadow-2xl border-4 border-amber-300">
                    {/* PDF Image */}
                    <div
                      className="relative w-full overflow-hidden rounded-2xl bg-white shadow-xl"
                      style={{ minHeight: 'min(calc(100dvh - 14rem), calc(100vh - 14rem))' }}
                    >
                      <img
                        src={pageImages[currentPage - 1].url}
                        alt={`Activity Page ${currentPage}`}
                        className="absolute inset-0 h-full w-full rounded-2xl bg-white object-contain object-center"
                        onContextMenu={(e) => e.preventDefault()}
                        draggable={false}
                        onLoad={(e) => {
                          console.log('✅ PDF image loaded successfully in expanded view:', pageImages[currentPage - 1].url);
                        }}
                        onError={(e) => {
                          console.error('❌ PDF image failed to load:', pageImages[currentPage - 1].url);
                        }}
                      />
                      
                      {/* Artwork — right overlay on all screen sizes */}
                      <div
                        className="absolute z-10 flex items-stretch justify-center pointer-events-none"
                        style={{ top: '4%', left: '50%', width: '49%', height: '92%' }}
                      >
                        <div className={`${overlayUrl ? 'h-full w-full' : 'h-full w-full min-h-0'} bg-gradient-to-br from-amber-200 via-orange-200 to-amber-300 rounded-xl border-2 border-amber-400 shadow-2xl overflow-hidden pointer-events-auto`}>
                          {overlayUrl ? (
                            <div className="relative flex h-full min-h-0 w-full items-center justify-center p-1">
                              <img 
                                src={overlayUrl} 
                                alt="Your artwork" 
                                className="max-h-full max-w-full h-full w-full object-contain rounded-lg"
                                draggable={false} 
                                onContextMenu={(e) => e.preventDefault()} 
                              />
                              <button
                                onClick={handleRemoveArtwork}
                                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors shadow-lg z-10"
                                title="Remove artwork"
                              >
                                <FaTrash size={12} />
                              </button>
                            </div>
                          ) : (
                            <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer p-4 sm:p-6 rounded-lg border-2 border-dashed border-amber-300 bg-white relative overflow-hidden">
                              <div className="text-center relative z-10">
                                <div className="text-4xl mb-4 text-amber-600">📝</div>
                                <div className="font-bold text-amber-800 text-lg mb-3">Upload Your Artwork</div>
                                <div className="text-amber-700 text-sm mb-4 leading-relaxed">
                                  Share your creative work to unlock the next activity
                                </div>
                                <div className="bg-gradient-to-r from-amber-500 to-amber-400 text-white px-6 py-3 rounded-lg hover:from-amber-600 hover:to-amber-500 transition-all duration-300 shadow-lg font-medium text-sm">
                                  Upload Artwork
                                </div>
                                <div className="mt-3 text-xs text-amber-600">
                                  Complete this activity to continue your journey
                                </div>
                              </div>
                              <input 
                                type="file" 
                                accept="image/*" 
                                onChange={handleOverlayUpload} 
                                disabled={uploading} 
                                className="hidden" 
                              />
                            </label>
                          )}
                          
                          {uploading && (
                            <div className="absolute inset-0 bg-gradient-to-br from-amber-200/90 to-orange-200/90 flex items-center justify-center rounded-lg">
                              <div className="text-center">
                                <FaSpinner className="animate-spin text-amber-600 mx-auto mb-3" size={32} />
                                <p className="text-amber-700 text-lg font-medium">Uploading your amazing artwork...</p>
                                <p className="text-amber-600 text-sm mt-1">We're so excited to see it!</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl shadow-lg flex items-center justify-center p-12" style={{ minHeight: '400px' }}>
                  <div className="text-center">
                    <div className="text-6xl mb-4">🎨</div>
                    <h3 className="text-xl font-semibold text-amber-700 mb-2">Activity {currentActivityId}</h3>
                    <p className="text-amber-600">PDF page not uploaded yet</p>
                  </div>
                </div>
              )}

              {/* Action buttons - Sticky at bottom */}
              <div className="sticky bottom-0 mt-6 bg-gradient-to-r from-amber-100 to-orange-100 border-t-2 border-amber-200 p-4 sm:p-5 rounded-t-xl md:rounded-t-2xl shadow-lg backdrop-blur-sm">
                <div className="flex flex-col sm:flex-row justify-center gap-2.5 sm:gap-3 max-w-4xl mx-auto">
                {overlayUrl ? (
                  !activityCompleted ? (
                    <>
                      {/* Show "Mark Complete" button when artwork is uploaded but not yet completed */}
                      <button
                        onClick={handleMarkComplete}
                        className="px-4 py-2 sm:px-5 sm:py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-lg shadow-md hover:shadow-lg font-medium text-sm sm:text-base transition-all duration-200 transform hover:scale-105 active:scale-95"
                      >
                        Mark Complete
                      </button>
                      <button
                        onClick={requestArtmosphereFeedback}
                        className="px-4 py-2 sm:px-5 sm:py-2.5 bg-white border-2 border-amber-500 text-amber-700 hover:bg-amber-50 rounded-lg shadow-md hover:shadow-lg font-medium text-sm sm:text-base transition-all duration-200 transform hover:scale-105 active:scale-95"
                      >
                        Artmosphere Feedback
                      </button>
                    </>
                  ) : shareModalCompleted ? (
                    <>
                      {/* Show "Next Activity" only after share modal is completed */}
                      <button
                        onClick={requestArtmosphereFeedback}
                        className="px-4 py-2 sm:px-5 sm:py-2.5 bg-white border-2 border-amber-500 text-amber-700 hover:bg-amber-50 rounded-lg shadow-md hover:shadow-lg font-medium text-sm sm:text-base transition-all duration-200 transform hover:scale-105 active:scale-95"
                      >
                        {existingActivity?.feedback ? 'New Artmosphere Feedback' : 'Artmosphere Feedback'}
                      </button>
                      {existingActivity?.feedback && (
                        <button
                          onClick={() => setShowFeedbackPreview(true)}
                          className="px-4 py-2 sm:px-5 sm:py-2.5 bg-white border-2 border-amber-400 text-amber-600 hover:bg-amber-50 rounded-lg shadow-md hover:shadow-lg font-medium text-sm sm:text-base transition-all duration-200 transform hover:scale-105 active:scale-95 flex items-center justify-center gap-1.5"
                        >
                          <FaEye size={14} />
                          <span className="hidden sm:inline">View</span>
                        </button>
                      )}
                      <button
                        onClick={async () => {
                          if (onNextActivity) {
                            await onNextActivity();
                          }
                        }}
                        className="px-4 py-2 sm:px-5 sm:py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-lg shadow-md hover:shadow-lg font-medium text-sm sm:text-base transition-all duration-200 transform hover:scale-105 active:scale-95"
                      >
                        Next Activity
                      </button>
                    </>
                  ) : (
                    <>
                      {/* Activity completed but share modal not yet shown/completed - show loading or wait state */}
                      <div className="text-center w-full">
                        <p className="text-amber-600 text-xs sm:text-sm">Processing your submission...</p>
                      </div>
                    </>
                  )
                ) : (
                  <div className="text-center w-full">
                    <label className="inline-flex items-center justify-center px-4 py-2 sm:px-5 sm:py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-lg shadow-md hover:shadow-lg font-medium text-sm sm:text-base transition-all duration-200 transform hover:scale-105 active:scale-95 cursor-pointer">
                      <FaUpload className="mr-2" size={14} />
                      Upload your artwork now
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleOverlayUpload}
                        disabled={uploading}
                        className="hidden"
                      />
                    </label>
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
      ) : (
        <div className="max-w-7xl mx-auto p-4">
          {/* Header with Expand/Collapse */}
          <div className="flex justify-between items-center mb-6">
            <div className="text-center flex-1">
              <h2 className="text-2xl font-bold text-amber-800 mb-2">
                Page {currentPage} of {pageImages.length || 1}
              </h2>
              <p className="text-amber-600 text-sm">Your creative adventure awaits!</p>
            </div>
            
            <button
              onClick={() => setInternalExpanded(!expanded)}
              className="bg-amber-600 text-white p-3 rounded-full hover:bg-amber-700 transition-colors shadow-lg"
              title="Expand to full screen"
            >
              <FaExpand size={20} />
            </button>
          </div>

          {/* Navigation */}
          <div className="flex justify-center gap-4 mb-6">
            <button
              onClick={prevPage}
              disabled={currentPage <= 1}
              className="px-6 py-3 bg-amber-600 text-white rounded-lg disabled:bg-amber-300 hover:bg-amber-700 transition-colors shadow-lg font-medium"
            >
              ← Previous
            </button>
            <button
              onClick={nextPage}
              disabled={currentPage >= (pageImages.length || 1)}
              className="px-6 py-3 bg-amber-600 text-white rounded-lg disabled:bg-amber-300 hover:bg-amber-700 transition-colors shadow-lg font-medium"
            >
              Next →
            </button>
          </div>

          {/* Main Content Area */}
          <div className="flex justify-center">
            <div className="relative group max-w-4xl w-full">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400/30 to-orange-400/30 rounded-3xl blur-2xl"></div>
              
              {/* Main container with slim amber frame */}
              <div className="relative bg-gradient-to-br from-amber-100 to-orange-100 rounded-3xl p-6 shadow-2xl border-4 border-amber-300">
                {/* PDF Image or Placeholder */}
                    <div className="relative mb-4 md:mb-6">
                  {pageImages.length > 0 ? (
                    <div
                      className="relative w-full overflow-hidden rounded-2xl"
                      style={{ minHeight: 'min(calc(100dvh - 12rem), 52vh)' }}
                    >
                      <img
                        src={pageImages[currentPage - 1]?.url}
                        alt={`Activity Page ${currentPage}`}
                        className="absolute inset-0 h-full w-full rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 object-contain object-center shadow-xl"
                        onContextMenu={(e) => e.preventDefault()}
                        draggable={false}
                      />
                      
                      {/* Artwork — right overlay on all screen sizes */}
                      <div
                        className="absolute z-10 flex items-stretch justify-center"
                        style={{ top: '4%', left: '50%', width: '49%', height: '92%' }}
                      >
                        <div className={`${overlayUrl ? 'h-full w-full' : 'h-full w-full min-h-0'} bg-gradient-to-br from-amber-200 via-orange-200 to-amber-300 rounded-xl border-2 border-amber-400 shadow-2xl overflow-hidden`}>
                          {overlayUrl ? (
                            <div className="relative flex h-full min-h-0 w-full items-center justify-center p-1">
                              <img 
                                src={overlayUrl} 
                                alt="Your artwork" 
                                className="max-h-full max-w-full h-full w-full object-contain rounded-lg"
                                draggable={false} 
                                onContextMenu={(e) => e.preventDefault()} 
                              />
                              <button
                                onClick={handleRemoveArtwork}
                                className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors shadow-lg"
                                title="Remove artwork"
                              >
                                <FaTrash size={12} />
                              </button>
                            </div>
                          ) : (
                            <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer p-6 rounded-lg border-2 border-dashed border-amber-300 bg-white relative overflow-hidden">
                              <div className="text-center relative z-10">
                                <div className="text-4xl mb-4 text-amber-600">📝</div>
                                <div className="font-bold text-amber-800 text-lg mb-3">Upload Your Artwork</div>
                                <div className="text-amber-700 text-sm mb-4 leading-relaxed">
                                  Share your creative work to unlock the next activity
                                </div>
                                <div className="bg-gradient-to-r from-amber-500 to-amber-400 text-white px-6 py-3 rounded-lg hover:from-amber-600 hover:to-amber-500 transition-all duration-300 shadow-lg font-medium text-sm">
                                  Upload Artwork
                                </div>
                                <div className="mt-3 text-xs text-amber-600">
                                  Complete this activity to continue your journey
                                </div>
                              </div>
                              <input 
                                type="file" 
                                accept="image/*" 
                                onChange={handleOverlayUpload} 
                                disabled={uploading} 
                                className="hidden" 
                              />
                            </label>
                          )}
                          
                          {uploading && (
                            <div className="absolute inset-0 bg-gradient-to-br from-amber-200/90 to-orange-200/90 flex items-center justify-center rounded-lg">
                              <div className="text-center">
                                <FaSpinner className="text-amber-600 mx-auto mb-3" size={32} />
                                <p className="text-amber-700 text-lg font-medium">Uploading your amazing artwork...</p>
                                <p className="text-amber-600 text-sm mt-1">We're so excited to see it!</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl shadow-xl flex items-center justify-center" style={{ minHeight: '400px' }}>
                      <div className="text-center">
                        <div className="text-6xl mb-4">🎨</div>
                        <h3 className="text-xl font-semibold text-amber-700 mb-2">Activity {currentActivityId}</h3>
                        <p className="text-amber-600">PDF page not uploaded yet</p>
                      </div>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex justify-center gap-4 mt-6 pt-6 border-t border-amber-200">
                    {overlayUrl ? (
                      !activityCompleted ? (
                        <>
                          {/* Show "Mark Complete" button when artwork is uploaded but not yet completed */}
                          <button
                            onClick={handleMarkComplete}
                            className="px-6 py-3 bg-amber-400 hover:bg-amber-500 text-white rounded-xl shadow-lg font-medium border-4 border-amber-200 transition-all duration-300 drop-shadow-xl"
                          >
                            Mark Complete
                          </button>
                          <button
                            onClick={() => setShowFeedbackModal(true)}
                            className="px-6 py-3 bg-amber-400 hover:bg-amber-500 text-white rounded-xl shadow-lg font-medium border-4 border-amber-200 transition-all duration-300 drop-shadow-xl"
                          >
                            Artmosphere Feedback
                          </button>
                        </>
                      ) : shareModalCompleted ? (
                        <>
                          {/* Show "Next Activity" only after share modal is completed */}
                          <button
                            onClick={requestArtmosphereFeedback}
                            className="px-6 py-3 bg-amber-400 hover:bg-amber-500 text-white rounded-xl shadow-lg font-medium border-4 border-amber-200 transition-all duration-300 drop-shadow-xl"
                          >
                            {existingActivity?.feedback ? 'Give New Feedback' : 'Artmosphere Feedback'}
                          </button>
                          {existingActivity?.feedback && (
                            <button
                              onClick={() => setShowFeedbackPreview(true)}
                              className="px-6 py-3 bg-amber-400 hover:bg-amber-500 text-white rounded-xl shadow-lg font-medium border-4 border-amber-200 transition-all duration-300 drop-shadow-xl flex items-center gap-2"
                            >
                              <FaEye size={16} />
                              View Previous Feedback
                            </button>
                          )}
                          <button
                            onClick={async () => {
                              if (onNextActivity) {
                                await onNextActivity();
                              }
                            }}
                            className="px-6 py-3 bg-amber-400 hover:bg-amber-500 text-white rounded-xl shadow-lg font-medium border-4 border-amber-200 transition-all duration-300 drop-shadow-xl"
                          >
                            Next Activity
                          </button>
                        </>
                      ) : (
                        <>
                          {/* Activity completed but share modal not yet shown/completed - show loading or wait state */}
                          <div className="text-center w-full">
                            <p className="text-amber-600 text-sm mb-2">Processing your submission...</p>
                          </div>
                        </>
                      )
                    ) : (
                      <div className="text-center">
                        <label className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl shadow-lg font-medium border-4 border-amber-200 transition-all duration-300 drop-shadow-xl cursor-pointer">
                          <FaUpload className="mr-2" size={16} />
                          Upload your artwork now
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleOverlayUpload}
                            disabled={uploading}
                            className="hidden"
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Artmosphere Feedback Modal */}
      <ArtmosphereFeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        activityData={currentActivityData || {
          title: `Activity ${currentActivityId}`,
          description: "Daily creative activity for developing artistic thinking",
          prerequisites: "Art supplies and imagination",
          steps: "Follow the activity instructions and create your artwork"
        }}
        userArtworkUrl={overlayUrl}
        activityPageUrl={pageImages[currentPage - 1]?.url}
        onFeedbackComplete={handleFeedbackComplete}
        onMarkComplete={handleMarkComplete}
        onReupload={handleReuploadBasedOnFeedback}
      />

      <UpgradeQuotaModal
        isOpen={showUpgradeModal}
        onClose={dismissUpgradeModal}
        feature={upgradeModalFeature}
        onUpgrade={() => { dismissUpgradeModal(); navigate('/membership'); }}
      />

      {/* Feedback Preview Modal */}
      {showFeedbackPreview && existingActivity?.feedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-amber-800">Previous Feedback</h2>
                <button
                  onClick={() => setShowFeedbackPreview(false)}
                  className="text-amber-600 hover:text-amber-800 transition-colors"
                >
                  <FaTimes size={24} />
                </button>
              </div>
              
              <div className="space-y-6">
                {existingActivity.feedback.artAnalysis && (
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 border-l-4 border-amber-400 shadow-lg">
                    <h4 className="text-xl font-semibold text-amber-700 mb-4">Art Analysis</h4>
                    <p className="text-amber-800 leading-relaxed text-lg">{existingActivity.feedback.artAnalysis}</p>
                  </div>
                )}
                
                {existingActivity.feedback.suggestions && existingActivity.feedback.suggestions.length > 0 && (
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 border-l-4 border-amber-500 shadow-lg">
                    <h4 className="text-xl font-semibold text-amber-700 mb-4">Suggestions</h4>
                    <ul className="space-y-3">
                      {existingActivity.feedback.suggestions.map((suggestion, index) => (
                        <li key={index} className="text-amber-800 leading-relaxed flex items-start">
                          <span className="text-amber-500 mr-3 text-lg">•</span>
                          <span className="text-amber-800">{suggestion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {existingActivity.feedback.techniques && existingActivity.feedback.techniques.length > 0 && (
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 border-l-4 border-orange-400 shadow-lg">
                    <h4 className="text-xl font-semibold text-orange-700 mb-4">Techniques Highlighted</h4>
                    <div className="flex flex-wrap gap-3">
                      {existingActivity.feedback.techniques.map((technique, index) => (
                        <span key={index} className="bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium border border-amber-200">
                          {technique}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {existingActivity.feedback.encouragement && (
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 border-l-4 border-amber-400 shadow-lg">
                    <h4 className="text-xl font-semibold text-amber-700 mb-4">Encouragement</h4>
                    <p className="text-amber-800 leading-relaxed text-lg">{existingActivity.feedback.encouragement}</p>
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowFeedbackPreview(false)}
                  className="px-6 py-3 bg-amber-400 hover:bg-amber-500 text-white rounded-xl shadow-lg font-medium border-4 border-amber-200 transition-all duration-300 drop-shadow-xl"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Learning Space Share Modal */}
      <LearningSpaceShareModal
        isOpen={showLearningSpaceModal}
        onClose={() => setShowLearningSpaceModal(false)}
        activityId={currentActivityId}
        imageUrl={uploadedImageUrl}
        activityData={currentActivityData}
        activityPageUrl={pageImages[currentPage - 1]?.url || ''}
        onShareComplete={async (isShared, submissionId) => {
          console.log('Learning Space share completed:', { isShared, submissionId });
          setShowLearningSpaceModal(false);
          setUploadedImageUrl(null);
          
          // Mark activity as completed and share modal as completed
          setActivityCompleted(true);
          setShareModalCompleted(true);
          
          if (onShareFlowComplete) {
            try {
              await onShareFlowComplete(isShared, submissionId);
            } catch (callbackError) {
              console.error('❌ Error in onShareFlowComplete:', callbackError);
            }
          } else if (onNextActivity) {
            try {
              await onNextActivity();
            } catch (callbackError) {
              console.error('❌ Error in onNextActivity callback:', callbackError);
            }
          }
        }}
      />
    </div>
  );
};

export default PDFPageDisplayForDailyActs;
